import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Eye } from "lucide-react";
import {
  getViewAsRole,
  setViewAsRole,
  clearViewAsRole,
  onViewAsRoleChange,
  SIMULATABLE_ROLES,
  SIMULATABLE_ROLE_LABELS
} from "../roleSimulator";

// Le o cache otimista "mosaico_user" (mesmo usado por PrototypeApp.js e
// InstitutionalLayout.jsx pra hidratar o topbar sem esperar /auth/me) so pra
// saber se QUEM ESTA DE VERDADE LOGADO e' ADMIN -- nunca o papel simulado,
// que fica so no localStorage separado "mosaico_admin_view_as_role" (ver
// roleSimulator.js). Sem rede extra: se o cache ainda nao existe (primeira
// visita antes do /auth/me responder), o controle simplesmente nao aparece
// ate a proxima re-checagem.
function getRealRolesFromCache() {
  try {
    const raw = window.localStorage.getItem("mosaico_user");
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed?.roles) ? parsed.roles : [];
  } catch {
    return [];
  }
}

const pillBaseStyle = {
  display: "inline-flex",
  alignItems: "center",
  gap: 7,
  border: "1px solid rgba(255,255,255,0.24)",
  borderRadius: 999,
  padding: "8px 14px",
  fontSize: 12,
  fontWeight: 900,
  cursor: "pointer",
  fontFamily: "inherit",
  boxShadow: "0 6px 18px rgba(0,0,0,0.28)"
};

const menuItemStyle = {
  display: "block",
  width: "100%",
  textAlign: "left",
  border: "none",
  background: "transparent",
  color: "#1f2937",
  padding: "9px 14px",
  fontSize: 13,
  fontWeight: 700,
  cursor: "pointer",
  fontFamily: "inherit"
};

// Posicao arrastavel do botao "Ver como" -- antes ficava sempre fixo em
// bottom:88/right:18 (so' deslocado pra nao tampar o avatar do Barnabe),
// mas em telas de celular menores ainda sobrava sobreposto em cima de
// menus/cards (reportado pelo Rodrigo: "o botao ver como e o barna ficam
// em cima de menus, n e' possivel ser movel como a bolinha da apple").
// Agora o botao pode ser arrastado (igual a bolinha do AssistiveTouch da
// Apple) e "gruda" na borda esquerda ou direita mais proxima ao soltar --
// a posicao (lado + altura) fica salva no localStorage pra sobreviver a
// reload.
const VIEWAS_DRAG_KEY = "mosaico_viewas_btn_pos_v1";
const DRAG_MARGIN = 8;

function loadViewAsDragPos() {
  try {
    const raw = window.localStorage.getItem(VIEWAS_DRAG_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function saveViewAsDragPos(pos) {
  try {
    window.localStorage.setItem(VIEWAS_DRAG_KEY, JSON.stringify(pos));
  } catch {
    // localStorage indisponivel (modo privado etc.) -- so' nao persiste
    // entre reloads, nao quebra o arrastar na mesma sessao.
  }
}

function clampDragPos(left, top, width, height) {
  const maxLeft = Math.max(DRAG_MARGIN, window.innerWidth - width - DRAG_MARGIN);
  const maxTop = Math.max(DRAG_MARGIN, window.innerHeight - height - DRAG_MARGIN);
  return {
    left: Math.min(Math.max(left, DRAG_MARGIN), maxLeft),
    top: Math.min(Math.max(top, DRAG_MARGIN), maxTop)
  };
}

// Controle de simulacao de papel, sempre disponivel pra quem tem ADMIN de
// verdade, em qualquer pagina do site (institucional, /familia) -- antes so
// dava pra ligar indo no Painel administrativo (/familia -> Admin). Continua
// montado uma vez, fora do switch de rotas (ver index.js), entao acompanha
// a navegacao inteira.
export default function ViewAsRoleBanner() {
  const [role, setRole] = useState(getViewAsRole());
  const [isRealAdmin, setIsRealAdmin] = useState(() => getRealRolesFromCache().includes("ADMIN"));
  const [menuOpen, setMenuOpen] = useState(false);
  const barRef = useRef(null);
  const fabWrapRef = useRef(null);
  const [dragPos, setDragPos] = useState(null); // {left, top} em px -- null = usa o bottom/right padrao
  const [dragSide, setDragSide] = useState(null); // "left" | "right" | null -- pra que lado o menu "Ver como" abre
  const [isDragging, setIsDragging] = useState(false);
  const dragStateRef = useRef(null);
  const suppressClickRef = useRef(false);

  // Restaura a posicao arrastada e salva (se existir) assim que o botao
  // "Ver como" monta -- so' faz sentido quando ele realmente aparece (ADMIN
  // de verdade, sem papel simulado ativo: mesmo guard isRealAdmin/role do
  // resto do componente).
  useLayoutEffect(() => {
    if (role || !isRealAdmin || !fabWrapRef.current) return;
    const saved = loadViewAsDragPos();
    if (!saved) return;
    const width = fabWrapRef.current.offsetWidth;
    const height = fabWrapRef.current.offsetHeight;
    const left = saved.side === "left" ? DRAG_MARGIN : window.innerWidth - width - DRAG_MARGIN;
    setDragPos(clampDragPos(left, saved.top, width, height));
    setDragSide(saved.side);
  }, [role, isRealAdmin]);

  // Reencaixa na borda certa se a janela mudar de tamanho (rotacionar o
  // celular, redimensionar no desktop) -- sem isso o botao podia ficar
  // fora da tela ou flutuando longe da borda depois de uma mudanca de
  // largura.
  useEffect(() => {
    if (role || !isRealAdmin) return undefined;
    const onResize = () => {
      const saved = loadViewAsDragPos();
      if (!saved || !fabWrapRef.current) return;
      const width = fabWrapRef.current.offsetWidth;
      const height = fabWrapRef.current.offsetHeight;
      const left = saved.side === "left" ? DRAG_MARGIN : window.innerWidth - width - DRAG_MARGIN;
      setDragPos(clampDragPos(left, saved.top, width, height));
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [role, isRealAdmin]);

  const handleFabPointerDown = (event) => {
    if (event.button !== undefined && event.button !== 0) return;
    if (!fabWrapRef.current) return;
    const rect = fabWrapRef.current.getBoundingClientRect();
    dragStateRef.current = {
      startX: event.clientX,
      startY: event.clientY,
      origLeft: rect.left,
      origTop: rect.top,
      moved: false,
      pointerId: event.pointerId
    };
    setIsDragging(true);
    try {
      event.currentTarget.setPointerCapture(event.pointerId);
    } catch {
      // navegador sem suporte a pointer capture -- o arrastar ainda
      // funciona, so' sem garantia de seguir o dedo fora do botao
    }
  };

  const handleFabPointerMove = (event) => {
    const drag = dragStateRef.current;
    if (!drag || !fabWrapRef.current) return;
    const dx = event.clientX - drag.startX;
    const dy = event.clientY - drag.startY;
    if (!drag.moved && (Math.abs(dx) > 6 || Math.abs(dy) > 6)) {
      drag.moved = true;
    }
    if (drag.moved) {
      event.preventDefault();
      const width = fabWrapRef.current.offsetWidth;
      const height = fabWrapRef.current.offsetHeight;
      setDragPos(clampDragPos(drag.origLeft + dx, drag.origTop + dy, width, height));
    }
  };

  const handleFabPointerEnd = (event) => {
    const drag = dragStateRef.current;
    setIsDragging(false);
    if (!drag) return;
    try {
      event.currentTarget.releasePointerCapture(drag.pointerId);
    } catch {
      // ver comentario equivalente em handleFabPointerDown
    }
    if (drag.moved && fabWrapRef.current) {
      // Gruda na borda mais proxima ao soltar, igual a bolinha do
      // AssistiveTouch da Apple -- sem isso o botao ficava largado no meio
      // da tela, atrapalhando o mesmo conteudo que o arrastar deveria
      // evitar.
      suppressClickRef.current = true;
      setTimeout(() => { suppressClickRef.current = false; }, 50);
      const rect = fabWrapRef.current.getBoundingClientRect();
      const side = rect.left + rect.width / 2 < window.innerWidth / 2 ? "left" : "right";
      const targetLeft = side === "left" ? DRAG_MARGIN : window.innerWidth - rect.width - DRAG_MARGIN;
      const clamped = clampDragPos(targetLeft, rect.top, rect.width, rect.height);
      setDragPos(clamped);
      setDragSide(side);
      saveViewAsDragPos({ side, top: clamped.top });
    }
    dragStateRef.current = null;
  };

  useEffect(() => onViewAsRoleChange(() => setRole(getViewAsRole())), []);

  // Publica a altura real da faixa "Vendo como" numa custom property CSS
  // (--viewas-offset) pro resto do site (topbar institucional, /familia,
  // /financeiro) empurrar menu e conteudo pra baixo em vez de ficar
  // escondido atras da faixa (que e' fixed com z-index bem alto). Sem isso
  // a faixa cobria o menu fixo, dificultando a navegacao (reportado pelo
  // Rodrigo). Reage a mudanca de papel e a resize (a faixa pode quebrar em
  // 2 linhas em telas estreitas).
  useLayoutEffect(() => {
    const root = document.documentElement;
    if (!role) {
      root.style.setProperty("--viewas-offset", "0px");
      return undefined;
    }
    const measure = () => {
      const height = barRef.current ? barRef.current.getBoundingClientRect().height : 0;
      root.style.setProperty("--viewas-offset", `${Math.ceil(height)}px`);
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [role]);

  useEffect(() => {
    return () => {
      // Este componente fica montado a vida inteira do app (fora do switch
      // de rotas -- ver index.js), mas se algum dia desmontar nao deve
      // deixar o resto do site compensando espaco de uma faixa que sumiu.
      document.documentElement.style.setProperty("--viewas-offset", "0px");
    };
  }, []);

  useEffect(() => {
    // "mosaico_user" e' escrito bem depois deste componente montar (login
    // acontece em qualquer pagina, de forma assincrona) -- reage ao evento
    // "storage" (outras abas) e tambem re-checa quando o proprio app troca o
    // cache nesta aba (esse evento nao dispara na mesma aba que escreveu).
    const recompute = () => setIsRealAdmin(getRealRolesFromCache().includes("ADMIN"));
    window.addEventListener("storage", recompute);
    // "mosaico_user" e' escrito na MESMA aba onde o login acontece, e o
    // evento nativo "storage" so dispara em OUTRAS abas -- por isso o
    // polling curto, so' pra pegar o momento em que o login confirma dentro
    // desta mesma aba (raro depois disso, ja que o valor so muda de novo no
    // login/logout).
    const interval = setInterval(recompute, 2000);
    return () => {
      window.removeEventListener("storage", recompute);
      clearInterval(interval);
    };
  }, []);

  const applyRole = (value) => {
    setMenuOpen(false);
    if (value) {
      setViewAsRole(value);
    } else {
      clearViewAsRole();
    }
    // Recarrega pra garantir que TODA a tela (nav, cards, gates) recalcula
    // com o papel novo -- so o ViewAsRoleBanner escuta onViewAsRoleChange;
    // o resto dos componentes le o papel simulado uma vez, no proprio mount.
    window.location.reload();
  };

  if (role) {
    return (
      <div
        ref={barRef}
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          zIndex: 9999,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 10,
          padding: "7px 14px",
          background: "#7C3AED",
          color: "#F5F3FF",
          fontSize: 12,
          fontWeight: 900,
          boxShadow: "0 2px 10px rgba(0,0,0,0.35)"
        }}
      >
        <Eye size={14} />
        <span>Vendo como: {SIMULATABLE_ROLE_LABELS[role] || role}</span>
        <select
          value={role}
          onChange={(event) => applyRole(event.target.value || null)}
          style={{
            background: "rgba(255,255,255,0.14)",
            color: "#F5F3FF",
            border: "1px solid rgba(255,255,255,0.4)",
            borderRadius: 999,
            padding: "3px 8px",
            fontSize: 11,
            fontWeight: 800
          }}
        >
          <optgroup label="Papéis">
            {SIMULATABLE_ROLES.filter((r) => !r.startsWith("ESCALA_")).map((r) => (
              <option key={r} value={r} style={{ color: "#1f2937" }}>
                {SIMULATABLE_ROLE_LABELS[r]}
              </option>
            ))}
          </optgroup>
          <optgroup label="Escalas por ministério">
            {SIMULATABLE_ROLES.filter((r) => r.startsWith("ESCALA_")).map((r) => (
              <option key={r} value={r} style={{ color: "#1f2937" }}>
                {SIMULATABLE_ROLE_LABELS[r]}
              </option>
            ))}
          </optgroup>
        </select>
        <button
          onClick={() => applyRole(null)}
          style={{
            border: "1px solid rgba(255,255,255,0.5)",
            background: "rgba(255,255,255,0.12)",
            color: "#F5F3FF",
            borderRadius: 999,
            padding: "3px 10px",
            fontSize: 11,
            fontWeight: 900,
            cursor: "pointer"
          }}
        >
          Sair do modo
        </button>
      </div>
    );
  }

  // So quem tem ADMIN de verdade ve o botao discreto pra COMECAR a simular
  // -- pra qualquer outra pessoa este componente nao renderiza nada.
  if (!isRealAdmin) return null;

  // Posicao padrao: bottom 88 (em vez de 18) pra nao ficar atras do avatar
  // do widget do Barnabe, que e' fixed no mesmo canto (right 16, bottom 16,
  // 60px) com z-index proposital bem alto -- reportado pelo Rodrigo vendo o
  // botao "Ver como" tampado pelo avatar em qualquer pagina (o widget
  // carrega em quase todo lugar, nao so' /admin). 88 = 16 (margem do
  // widget) + 60 (altura do avatar) + 12 (respiro) arredondado. Isso agora
  // e' so' a posicao INICIAL -- o botao pode ser arrastado (ver
  // handleFabPointer* acima), igual a bolinha do AssistiveTouch da Apple,
  // porque em telas de celular menores mesmo esse ajuste ainda deixava o
  // botao (e o avatar do Barna) em cima de menus/cards em algumas paginas
  // (reportado pelo Rodrigo, 21/10/2026: "o botao ver como e o barna ficam
  // em cima de menus, n e' possivel ser movel como a bolinha da apple").
  const menuOpensBelow = Boolean(dragPos && dragPos.top < 240);
  const fabWrapStyle = dragPos
    ? {
        position: "fixed",
        left: dragPos.left,
        top: dragPos.top,
        zIndex: 9999,
        transition: isDragging ? "none" : "left 0.18s ease, top 0.18s ease"
      }
    : { position: "fixed", bottom: 88, right: 18, zIndex: 9999 };

  return (
    <div ref={fabWrapRef} style={fabWrapStyle}>
      {menuOpen && (
        <div
          style={{
            position: "absolute",
            ...(menuOpensBelow ? { top: "calc(100% + 8px)" } : { bottom: "calc(100% + 8px)" }),
            ...(dragSide === "left" ? { left: 0 } : { right: 0 }),
            minWidth: 220,
            background: "#fff",
            borderRadius: 12,
            boxShadow: "0 12px 32px rgba(0,0,0,0.28)",
            overflow: "hidden",
            border: "1px solid rgba(15,23,42,0.08)"
          }}
        >
          <div style={{ maxHeight: "min(70vh, 480px)", overflowY: "auto" }}>
            <div style={{ padding: "9px 14px", fontSize: 11, fontWeight: 900, color: "#94a3b8", textTransform: "uppercase", letterSpacing: 0.6 }}>
              Papéis
            </div>
            {SIMULATABLE_ROLES.filter((r) => !r.startsWith("ESCALA_")).map((r) => (
              <button key={r} type="button" style={menuItemStyle} onClick={() => applyRole(r)}>
                {SIMULATABLE_ROLE_LABELS[r]}
              </button>
            ))}
            <div style={{ padding: "9px 14px", fontSize: 11, fontWeight: 900, color: "#94a3b8", textTransform: "uppercase", letterSpacing: 0.6, borderTop: "1px solid rgba(15,23,42,0.08)", marginTop: 4 }}>
              Escalas por ministério
            </div>
            {SIMULATABLE_ROLES.filter((r) => r.startsWith("ESCALA_")).map((r) => (
              <button key={r} type="button" style={menuItemStyle} onClick={() => applyRole(r)}>
                {SIMULATABLE_ROLE_LABELS[r]}
              </button>
            ))}
          </div>
        </div>
      )}
      <button
        type="button"
        onPointerDown={handleFabPointerDown}
        onPointerMove={handleFabPointerMove}
        onPointerUp={handleFabPointerEnd}
        onPointerCancel={handleFabPointerEnd}
        onClick={() => {
          if (suppressClickRef.current) return;
          setMenuOpen((open) => !open);
        }}
        style={{ ...pillBaseStyle, background: "#1f2937", color: "#F1F5F9", touchAction: "none", cursor: isDragging ? "grabbing" : "grab" }}
      >
        <Eye size={14} />
        Ver como
      </button>
    </div>
  );
}
